/**
 * Pure handlers for aswb-backend `publicDiagnosticSimulationBeat` /
 * `publicDiagnosticAskPam` (#580 Lane C). Copy into the backend callable
 * wrappers; keep env `PUBLIC_DIAGNOSTIC_LLM_DAILY_CAP_USD`.
 *
 * Cap / rate-limit → degrade (llmUsed:false), never throw to the client.
 */
import {
  ASK_PAM_DEGRADED_MARKDOWN,
  ASK_PAM_PROMPT_MAX,
  ASK_PAM_REFUSE_MARKDOWN,
  IP_RATE_LIMIT,
  LaneCDailySpendMeter,
  LaneCRateLimiter,
  PUBLIC_DIAGNOSTIC_BEAT,
  SESSION_RATE_LIMIT,
  classifyAskPamPrompt,
  readDailyCapUsd,
  simulationFeedback,
  truncateAskPamPrompt,
} from './laneCPolicy';

export interface LaneCBackendDeps {
  sessionLimiter?: LaneCRateLimiter;
  ipLimiter?: LaneCRateLimiter;
  spendMeter?: LaneCDailySpendMeter;
  /** Optional LLM. Return null to force degrade. */
  invokeLlm?: (kind: 'simulation' | 'askPam', prompt: string) => Promise<string | null>;
  /** Estimated USD charged when attempting LLM. */
  llmCostUsd?: number;
}

const defaultSessionLimiter = new LaneCRateLimiter();
const defaultIpLimiter = new LaneCRateLimiter();

function meterFromEnv(existing?: LaneCDailySpendMeter): LaneCDailySpendMeter {
  return existing ?? new LaneCDailySpendMeter(readDailyCapUsd());
}

function trafficAllowed(
  sessionId: string,
  ipKey: string,
  sessionLimiter: LaneCRateLimiter,
  ipLimiter: LaneCRateLimiter
): boolean {
  return (
    sessionLimiter.allow(`sess:${sessionId}`, SESSION_RATE_LIMIT.max, SESSION_RATE_LIMIT.windowMs) &&
    ipLimiter.allow(`ip:${ipKey}`, IP_RATE_LIMIT.max, IP_RATE_LIMIT.windowMs)
  );
}

export async function handlePublicDiagnosticSimulationBeat(
  input: { sessionId: string; ipKey: string; scenarioId?: string; decisionId: string },
  deps: LaneCBackendDeps = {}
): Promise<{
  scenarioId: string;
  decisionId: string;
  feedbackMarkdown: string;
  llmUsed: boolean;
  rateLimited?: boolean;
  degraded?: boolean;
}> {
  const sessionLimiter = deps.sessionLimiter ?? defaultSessionLimiter;
  const ipLimiter = deps.ipLimiter ?? defaultIpLimiter;
  const spend = meterFromEnv(deps.spendMeter);
  const scenarioId = input.scenarioId || PUBLIC_DIAGNOSTIC_BEAT.scenarioId;
  const local = simulationFeedback(input.decisionId);

  if (!trafficAllowed(input.sessionId, input.ipKey, sessionLimiter, ipLimiter)) {
    return {
      scenarioId,
      decisionId: input.decisionId,
      feedbackMarkdown: local,
      llmUsed: false,
      rateLimited: true,
      degraded: true,
    };
  }

  const cost = deps.llmCostUsd ?? 0.002;
  if (!spend.tryCharge(cost) || !deps.invokeLlm) {
    return {
      scenarioId,
      decisionId: input.decisionId,
      feedbackMarkdown: local,
      llmUsed: false,
      degraded: true,
    };
  }

  try {
    const llm = await deps.invokeLlm(
      'simulation',
      `Decision ${input.decisionId} for scenario ${scenarioId}`
    );
    if (!llm?.trim()) {
      return {
        scenarioId,
        decisionId: input.decisionId,
        feedbackMarkdown: local,
        llmUsed: false,
        degraded: true,
      };
    }
    return {
      scenarioId,
      decisionId: input.decisionId,
      feedbackMarkdown: llm.trim(),
      llmUsed: true,
    };
  } catch {
    return {
      scenarioId,
      decisionId: input.decisionId,
      feedbackMarkdown: local,
      llmUsed: false,
      degraded: true,
    };
  }
}

export async function handlePublicDiagnosticAskPam(
  input: { sessionId: string; ipKey: string; prompt: string },
  deps: LaneCBackendDeps = {}
): Promise<{
  prompt: string;
  answerMarkdown: string;
  llmUsed: boolean;
  refused?: boolean;
  rateLimited?: boolean;
  degraded?: boolean;
}> {
  const prompt = truncateAskPamPrompt(input.prompt).slice(0, ASK_PAM_PROMPT_MAX);
  if (classifyAskPamPrompt(prompt) === 'refuse') {
    return {
      prompt,
      answerMarkdown: ASK_PAM_REFUSE_MARKDOWN,
      llmUsed: false,
      refused: true,
    };
  }

  const sessionLimiter = deps.sessionLimiter ?? defaultSessionLimiter;
  const ipLimiter = deps.ipLimiter ?? defaultIpLimiter;
  const spend = meterFromEnv(deps.spendMeter);

  if (!trafficAllowed(input.sessionId, input.ipKey, sessionLimiter, ipLimiter)) {
    return {
      prompt,
      answerMarkdown: ASK_PAM_DEGRADED_MARKDOWN,
      llmUsed: false,
      rateLimited: true,
      degraded: true,
    };
  }

  const cost = deps.llmCostUsd ?? 0.004;
  if (!spend.tryCharge(cost) || !deps.invokeLlm) {
    return {
      prompt,
      answerMarkdown: ASK_PAM_DEGRADED_MARKDOWN,
      llmUsed: false,
      degraded: true,
    };
  }

  try {
    const answer = await deps.invokeLlm('askPam', prompt);
    if (!answer?.trim()) {
      return {
        prompt,
        answerMarkdown: ASK_PAM_DEGRADED_MARKDOWN,
        llmUsed: false,
        degraded: true,
      };
    }
    return { prompt, answerMarkdown: answer.trim(), llmUsed: true };
  } catch {
    return {
      prompt,
      answerMarkdown: ASK_PAM_DEGRADED_MARKDOWN,
      llmUsed: false,
      degraded: true,
    };
  }
}
